import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatNaira, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Loader2, X, Download, Upload } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = {
  name: "", description: "", unit: "", category_slug: "", price: 0, sku: "", stock: 0,
  level: "", brand: "", images: [], featured: false, active: true,
};

export default function AdminProducts() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [imageInput, setImageInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState("");
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imgFileRef = useRef(null);
  const [exportFormat, setExportFormat] = useState("csv");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [downloadingTpl, setDownloadingTpl] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["admin-products"],
    queryFn: () => api.get("/products", { params: { include_inactive: true, limit: 500 } }).then((r) => r.data),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: () => api.get("/categories", { params: { include_inactive: true } }).then((r) => r.data),
  });

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, category_slug: categories[0]?.slug || "" });
    setImageInput("");
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p });
    setImageInput("");
    setDialogOpen(true);
  };

  const addImage = () => {
    if (imageInput.trim()) {
      setForm({ ...form, images: [...form.images, imageInput.trim()] });
      setImageInput("");
    }
  };
  const removeImage = (i) => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.name || !form.category_slug) {
      toast.error("Name and category are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      stock: Number(form.stock) || 0,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      setDialogOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/products/${deleteId}`);
      toast.success("Product deleted");
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleteId(null);
    }
  };

  const downloadBlob = (data, filename, type) => {
    const url = URL.createObjectURL(new Blob([data], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      const n = new Set(s);
      if (filtered.every((p) => n.has(p.id))) filtered.forEach((p) => n.delete(p.id));
      else filtered.forEach((p) => n.add(p.id));
      return n;
    });
  };
  const doBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await api.post("/admin/products/bulk-delete", { ids: [...selected] });
      toast.success(`${res.data.deleted} product${res.data.deleted === 1 ? "" : "s"} deleted`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBulkDeleting(false);
      setBulkConfirm(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const isCsv = exportFormat === "csv";
      const endpoint = isCsv ? "/admin/catalog/export-csv" : "/admin/catalog/export";
      const res = await api.get(endpoint, {
        params: exportScope ? { category: exportScope } : {},
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : (isCsv ? "booksnmore-products.csv" : "booksnmore-catalog.json");
      downloadBlob(res.data, filename, isCsv ? "text/csv" : "application/json");
      toast.success("Catalogue exported");
      setExportOpen(false);
    } catch (err) {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const downloadTemplate = async () => {
    setDownloadingTpl(true);
    try {
      const res = await api.get("/admin/catalog/template-csv", { responseType: "blob" });
      downloadBlob(res.data, "booksnmore-product-template.csv", "text/csv");
      toast.success("Template downloaded");
    } catch (err) {
      toast.error("Could not download template.");
    } finally {
      setDownloadingTpl(false);
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const isCsv = /\.csv$/i.test(file.name);
      const endpoint = isCsv ? "/admin/catalog/import-csv" : "/admin/catalog/import";
      const res = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
      const p = res.data.products;
      toast.success(`Import complete — ${p.inserted} added, ${p.updated} updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      if ((fileList || []).length) toast.error("Only image files can be uploaded.");
      return;
    }
    setUploadingImage(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, res.data.url] }));
        ok += 1;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
    if (ok > 0) toast.success(`${ok} image${ok === 1 ? "" : "s"} uploaded`);
    setUploadingImage(false);
  };

  const uploadImage = (e) => {
    uploadFiles(e.target.files);
    e.target.value = "";
  };

  const onImageDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-black text-3xl text-slate-900">Products</h1>
          <p className="text-slate-500">{products.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" accept=".json,.csv,application/json,text/csv" onChange={onImportFile} className="hidden" data-testid="import-file-input" />
          <button onClick={downloadTemplate} disabled={downloadingTpl} data-testid="download-template-btn" className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-50 disabled:opacity-60">
            {downloadingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Template
          </button>
          <button onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-catalog-btn" className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-50 disabled:opacity-60">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
          </button>
          <button onClick={() => { setExportScope(""); setExportOpen(true); }} data-testid="export-catalog-btn" className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-50">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={openNew} data-testid="add-product-btn" className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-blue/90">
            <Plus className="w-4 h-4" /> Add product
          </button>
        </div>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input data-testid="product-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products" className="w-full pl-10 pr-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 px-4 py-3 bg-brand-blue/5 border border-brand-blue/20 rounded-lg" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold text-brand-blue">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn" className="text-sm font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5">Clear</button>
            <button onClick={() => setBulkConfirm(true)} data-testid="bulk-delete-btn" className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-red-700">
              <Trash2 className="w-4 h-4" /> Delete selected
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3 w-10"><input type="checkbox" data-testid="select-all-products" checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))} onChange={toggleAll} className="w-4 h-4 align-middle" /></th>
                  <th className="px-4 py-3 font-semibold">Product</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Price</th>
                  <th className="px-4 py-3 font-semibold">Stock</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((p) => (
                  <tr key={p.id} className={`hover:bg-slate-50 ${selected.has(p.id) ? "bg-brand-blue/5" : ""}`} data-testid={`product-row-${p.slug}`}>
                    <td className="px-4 py-3"><input type="checkbox" data-testid={`select-product-${p.slug}`} checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} className="w-4 h-4 align-middle" /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded object-cover border" />}
                        <span className="font-medium text-slate-800">{p.name}{p.featured && <span className="ml-2 text-xs text-brand-gold font-bold">★</span>}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-600">{p.category_slug?.replace(/-/g, " ")}</td>
                    <td className="px-4 py-3 font-semibold">{formatNaira(p.price)}</td>
                    <td className="px-4 py-3"><span className={p.stock <= 5 ? "text-red-600 font-semibold" : "text-slate-600"}>{p.stock}</span></td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Active" : "Hidden"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.slug}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteId(p.id)} data-testid={`delete-product-${p.slug}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-400">No products found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Name *</label>
              <input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Description</label>
              <textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Category *</label>
                <select data-testid="form-category" value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30">
                  <option value="">Select...</option>
                  {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Price (₦)</label>
                <input data-testid="form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Stock</label>
                <input data-testid="form-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">SKU</label>
                <input data-testid="form-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Level (educational)</label>
                <select data-testid="form-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30">
                  <option value="">N/A</option>
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="university">University</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Brand</label>
                <input data-testid="form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Unit of measure</label>
              <input data-testid="form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g. Pack of 12" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Images</label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                onDrop={onImageDrop}
                data-testid="image-dropzone"
                className={`rounded-lg border-2 border-dashed p-4 transition-colors ${dragActive ? "border-brand-blue bg-brand-blue/5" : "border-slate-300"}`}
              >
                <div className="flex gap-2 items-center flex-wrap">
                  <input ref={imgFileRef} type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadImage} className="hidden" data-testid="form-image-file" />
                  <button type="button" onClick={() => imgFileRef.current?.click()} disabled={uploadingImage} data-testid="form-upload-image" className="inline-flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg font-medium disabled:opacity-60">
                    {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload photos
                  </button>
                  <span className="text-xs text-slate-400">Drag &amp; drop images here, or click to browse · JPG, PNG, WEBP · max 8MB each</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <input data-testid="form-image-input" value={imageInput} onChange={(e) => setImageInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImage())} placeholder="…or paste an image URL" className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
                  <button type="button" onClick={addImage} data-testid="form-add-image" className="px-4 bg-slate-800 text-white rounded-lg font-medium">Add</button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative w-16 h-16 rounded border overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-red-600 text-white p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input data-testid="form-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" /> Active (visible on site)
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 rounded-lg border font-medium">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-product-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brand-blue text-white font-semibold disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete product?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-product" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export catalogue</DialogTitle>
            <DialogDescription>Download your products to edit in a spreadsheet (CSV) or to move between environments (JSON).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Format</label>
              <select data-testid="export-format-select" value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} className="w-full px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30">
                <option value="csv">CSV (spreadsheet — Excel / Google Sheets)</option>
                <option value="json">JSON (full catalogue, for syncing environments)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">What to export</label>
              <select data-testid="export-scope-select" value={exportScope} onChange={(e) => setExportScope(e.target.value)} className="w-full px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30">
                <option value="">All products</option>
                {categories.map((c) => <option key={c.id} value={c.slug}>{c.name} only</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setExportOpen(false)} className="px-4 py-2 rounded-lg border font-medium">Cancel</button>
            <button onClick={doExport} disabled={exporting} data-testid="confirm-export-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brand-blue text-white font-semibold disabled:opacity-60">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!importResult} onOpenChange={() => setImportResult(null)}>
        <DialogContent className="max-w-md" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle>Import complete</DialogTitle>
            <DialogDescription>Matching products were updated, new ones added. Nothing was deleted.</DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-semibold text-slate-700 mb-1">Products</p>
                <ul className="text-slate-600 space-y-0.5">
                  <li><span className="font-semibold text-green-700">{importResult.products.inserted}</span> new added</li>
                  <li><span className="font-semibold text-blue-700">{importResult.products.updated}</span> updated</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-700 mb-1">Categories</p>
                <ul className="text-slate-600 space-y-0.5">
                  <li><span className="font-semibold text-green-700">{importResult.categories.inserted}</span> new added</li>
                  <li><span className="font-semibold text-blue-700">{importResult.categories.updated}</span> updated</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setImportResult(null)} data-testid="import-result-close" className="px-5 py-2 rounded-lg bg-brand-blue text-white font-semibold">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} product{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the selected products and releases their uploaded images. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doBulkDelete(); }} disabled={bulkDeleting} data-testid="confirm-bulk-delete" className="bg-red-600 hover:bg-red-700">
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
